import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from './useUserRole';

export interface ChurchBrandingData {
  id: string;
  name: string;
  logo_url?: string;
  primary_color: string;
  secondary_color: string;
}

export const useChurchBranding = () => {
  const { roles, isSuperAdmin } = useUserRole();
  
  // Get the first church for church admins (they should only have one)
  const churchId = !isSuperAdmin && roles.length > 0 ? roles[0].church_id : null;

  const { data: church, isLoading } = useQuery({
    queryKey: ['church-branding', churchId],
    queryFn: async () => {
      if (!churchId) return null;
      
      const { data, error } = await supabase
        .from('churches')
        .select('id, name, logo_url, primary_color, secondary_color')
        .eq('id', churchId)
        .maybeSingle();

      if (error) throw error;
      return data as ChurchBrandingData | null;
    },
    enabled: !!churchId && !isSuperAdmin,
  });

  // Apply church branding colors to CSS variables
  useEffect(() => {
    if (church && !isSuperAdmin) {
      const root = document.documentElement;
      
      // Convert hex colors to HSL
      const hexToHsl = (hex: string) => {
        // Remove the hash if present
        hex = hex.replace('#', '');
        
        // Parse RGB values
        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;
        
        // Find min and max values
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0, l = (max + min) / 2;
        
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          
          switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
          }
          h /= 6;
        }
        
        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      };

      try {
        // Apply primary color
        if (church.primary_color) {
          const primaryHsl = hexToHsl(church.primary_color);
          root.style.setProperty('--primary', primaryHsl);
          root.style.setProperty('--sidebar-primary', primaryHsl);
        }
        
        // Apply secondary color as accent
        if (church.secondary_color) {
          const secondaryHsl = hexToHsl(church.secondary_color);
          root.style.setProperty('--accent', secondaryHsl);
        }
      } catch (error) {
        console.warn('Error applying church branding colors:', error);
      }
    }
    
    // Cleanup function to reset colors when component unmounts or user changes
    return () => {
      if (isSuperAdmin || !church) {
        const root = document.documentElement;
        root.style.removeProperty('--primary');
        root.style.removeProperty('--sidebar-primary');
        root.style.removeProperty('--accent');
      }
    };
  }, [church, isSuperAdmin]);

  return {
    church: !isSuperAdmin ? church : null,
    isLoading: !isSuperAdmin ? isLoading : false,
  };
};